import { RouteStop } from "@/reducers/routes2gSlice"

export const STOP_NUMBERS_FLAG = "map-stop-numbers"

// Stops this close together are one pin on the map. Two stops a list resolves
// to the same address land on the very same point; 30 m also catches the same
// gate geocoded twice.
const SAME_PLACE_METERS = 30

const isNumbered = (stop: RouteStop) =>
  stop.proximity_status !== "left" &&
  stop.proximity_status !== "skipped" &&
  stop.action !== "passthrough" &&
  stop.action !== "from_driver_location"

/**
 * Each stop's place in what is still ahead of the driver: 1 is the next stop.
 * Stops already passed or skipped, and points that only shape the route, get
 * no number.
 */
export function stopNumbers(stops: RouteStop[]): (number | undefined)[] {
  let next = 1
  return stops.map((stop) => (isNumbered(stop) ? next++ : undefined))
}

const coordinates = (stop: RouteStop): [number, number] | null => {
  const lat = Number(stop.location?.lat)
  const lng = Number(stop.location?.lon)
  if (!stop.location?.lat || !stop.location?.lon) return null
  return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null
}

const metersBetween = (
  [lat1, lng1]: [number, number],
  [lat2, lng2]: [number, number],
) => {
  const toRad = Math.PI / 180
  const x = (lng2 - lng1) * toRad * Math.cos(((lat1 + lat2) / 2) * toRad)
  const y = (lat2 - lat1) * toRad
  return Math.sqrt(x * x + y * y) * 6371000
}

/**
 * The badge each stop's pin carries. Stops at one place draw their icons on
 * top of each other, so the first of them shows every number there ("1 · 3")
 * and the others show none.
 */
export function stopPinLabels(stops: RouteStop[]): (string | undefined)[] {
  const numbers = stopNumbers(stops)
  const labels: (string | undefined)[] = stops.map(() => undefined)
  const groups: {
    at: [number, number] | null
    first: number
    numbers: number[]
  }[] = []

  stops.forEach((stop, index) => {
    const number = numbers[index]
    if (number === undefined) return
    const at = coordinates(stop)
    const group = at
      ? groups.find((g) => g.at && metersBetween(g.at, at) <= SAME_PLACE_METERS)
      : undefined
    if (group) group.numbers.push(number)
    else groups.push({ at, first: index, numbers: [number] })
  })

  groups.forEach((group) => {
    labels[group.first] = group.numbers.join(" · ")
  })
  return labels
}
